import type { AppLayoutProps } from '@cloudscape-design/components';

/**
 * How wide the assistant drawer opens, and remembering how wide the user made it.
 *
 * The drawer shipped at 480px, chosen when the assistant could render nothing
 * but prose. It now renders real data tables (and soon charts), and 480 clips
 * the last column of a modest table -- one user lost a column and only found
 * the resize handle by hunting for it.
 */

/**
 * 720px. Wide enough for a five-or-six-column Cloudscape table plus the panel's
 * own padding, and still only half of a 1440px laptop viewport, so the app the
 * assistant is answering *about* stays readable beside it. Going wider by
 * default would cover the page the user is asking about; going narrower brings
 * the clipped-column problem straight back.
 */
export const ASSISTANT_DEFAULT_WIDTH = 720;

/** Narrower than this and the chat composer itself starts to wrap badly. */
export const ASSISTANT_MIN_WIDTH = 320;

/**
 * A remembered width above this is almost certainly stale (it came from a much
 * larger display, or a corrupted value) and would open the drawer over the
 * whole page on a laptop. Cloudscape also clamps to the viewport at render
 * time; this is just to keep an absurd stored number out of the DOM.
 */
export const ASSISTANT_MAX_WIDTH = 1600;

export const ASSISTANT_WIDTH_STORAGE_KEY = 'gp.assistant.drawerWidth';

function clamp(size: number): number {
  return Math.min(ASSISTANT_MAX_WIDTH, Math.max(ASSISTANT_MIN_WIDTH, Math.round(size)));
}

/**
 * The width the drawer should open at: whatever the user last dragged to, or
 * {@link ASSISTANT_DEFAULT_WIDTH}.
 *
 * Every storage access is guarded. `localStorage` throws outright when storage
 * is disabled by policy or unavailable in a private window, and a drawer that
 * cannot remember a width must still open.
 */
export function readAssistantWidth(): number {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(ASSISTANT_WIDTH_STORAGE_KEY);
  } catch {
    return ASSISTANT_DEFAULT_WIDTH;
  }
  if (stored === null) return ASSISTANT_DEFAULT_WIDTH;

  const parsed = Number(stored);
  // Number('') is 0 and Number('Infinity') is Infinity, so a plain NaN check is
  // not enough -- anything that is not a finite positive number is ignored.
  if (!Number.isFinite(parsed) || parsed <= 0) return ASSISTANT_DEFAULT_WIDTH;
  return clamp(parsed);
}

/** Remembers a width the user dragged to. A nonsense size is dropped, not stored. */
export function rememberAssistantWidth(size: number): void {
  if (!Number.isFinite(size) || size <= 0) return;
  try {
    window.localStorage.setItem(ASSISTANT_WIDTH_STORAGE_KEY, String(clamp(size)));
  } catch {
    /* Storage disabled or full: the width just will not survive this reload. */
  }
}

/**
 * The two AppLayout drawer props that make the width stick.
 *
 * Cloudscape DOES surface the drawer width: `AppLayoutProps.Drawer.onResize`
 * fires with `{ size }` on every drag, and `defaultSize` is only consulted
 * until the user resizes (after that AppLayout's own `drawerSizes` state wins,
 * see `app-layout/utils/use-drawers.js`). So re-reading storage on each render
 * is safe -- it cannot snap an in-progress drag back.
 */
export function assistantDrawerSizing(): Pick<AppLayoutProps.Drawer, 'defaultSize' | 'onResize'> {
  return {
    defaultSize: readAssistantWidth(),
    onResize: (event) => rememberAssistantWidth(event.detail.size),
  };
}
